import { ReactNode, useEffect, useState } from "react";
import { NavLink, useNavigate, Link, Outlet, useLocation, matchPath } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Inbox,
  ScrollText,
  Settings,
  LogOut,
  User,
  Package,
  FolderTree,
  List,
  Percent,
  Truck,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePendingRequestsCount } from "@/hooks/usePendingRequestsCount";
import { supabase } from "@/integrations/supabase/client";

interface AdminLayoutProps {
  title?: string;
  children?: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  showBadge?: boolean;
  disabled?: boolean;
}

interface NavGroup {
  label?: string;
  icon?: typeof LayoutDashboard;
  collapsible?: boolean;
  defaultOpen?: boolean;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
    ],
  },
  {
    label: "Catalog",
    icon: Package,
    collapsible: true,
    defaultOpen: true,
    items: [
      { to: "/catalog/categories", label: "Browse Categories", icon: FolderTree },
      { to: "/catalog/system-items", label: "System Line Items", icon: List },
      { to: "/catalog/markup-tiers", label: "Markup Tiers", icon: Percent },
      { to: "/catalog/suppliers", label: "Suppliers", icon: Truck },
    ],
  },
  {
    label: "Companies & Users",
    icon: Building2,
    collapsible: true,
    defaultOpen: false,
    items: [
      { to: "/companies", label: "Companies", icon: Building2 },
      { to: "/users", label: "Users", icon: Users },
    ],
  },
  {
    label: "Operations",
    icon: ScrollText,
    collapsible: true,
    defaultOpen: false,
    items: [
      { to: "/requests", label: "Requests", icon: Inbox, showBadge: true },
      { to: "/audit", label: "Audit Log", icon: ScrollText },
    ],
  },
  {
    items: [
      { to: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

const TITLE_ROUTES: { pattern: string; title: string }[] = [
  { pattern: "/", title: "Dashboard" },
  { pattern: "/users", title: "Users" },
  { pattern: "/companies", title: "Companies" },
  { pattern: "/requests", title: "Requests" },
  { pattern: "/audit", title: "Audit Log" },
  { pattern: "/settings", title: "Settings" },
  { pattern: "/profile", title: "Profile" },
  { pattern: "/photo-migration", title: "Photo Migration" },
  { pattern: "/catalog/categories", title: "Browse Categories" },
  { pattern: "/catalog/system-items/new", title: "Add System Item" },
  { pattern: "/catalog/system-items/:id/edit", title: "Edit System Item" },
  { pattern: "/catalog/system-items", title: "System Line Items" },
  { pattern: "/catalog/markup-tiers", title: "Markup Tiers" },
  { pattern: "/catalog/suppliers", title: "Platform Suppliers" },
];

function resolveTitle(pathname: string): string {
  for (const r of TITLE_ROUTES) {
    if (matchPath({ path: r.pattern, end: true }, pathname)) return r.title;
  }
  return "";
}

export default function AdminLayout({ title, children }: AdminLayoutProps) {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: pendingCount = 0 } = usePendingRequestsCount();
  const [displayName, setDisplayName] = useState("");

  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>(() => {
    const initial: Record<number, boolean> = {};
    navGroups.forEach((g, i) => {
      if (g.collapsible) initial[i] = g.defaultOpen ?? false;
    });
    return initial;
  });

  const toggleGroup = (idx: number) => {
    setOpenGroups((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const resolvedTitle = title ?? resolveTitle(location.pathname);

  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) { setDisplayName(""); return; }
    supabase
      .from("profiles")
      .select("first_name, last_name")
      .eq("id", uid)
      .single()
      .then(({ data }) => {
        if (data?.first_name) {
          setDisplayName(`${data.first_name} ${data.last_name ?? ""}`.trim());
        }
      });
  }, [session?.user?.id]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const email = session?.user?.email ?? "";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-60 bg-slate-900 border-r border-slate-800 flex flex-col">
        {/* Brand */}
        <div className="px-4 py-4 border-b border-slate-800">
          <div className="text-sm font-bold text-white leading-tight">Doyence</div>
          <div className="text-xs text-slate-400 leading-tight">Admin Portal</div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          {navGroups.map((group, gIdx) => {
            const isCollapsible = group.collapsible;
            const isOpen = !isCollapsible || openGroups[gIdx];
            const hasLabel = !!group.label;

            return (
              <div key={gIdx} className={gIdx > 0 ? "mt-1" : ""}>
                {hasLabel &&
                  (isCollapsible ? (
                    <button
                      type="button"
                      onClick={() => toggleGroup(gIdx)}
                      className="w-full flex items-center justify-between px-2.5 py-1.5 text-sm font-medium text-slate-300 hover:bg-slate-800/60 hover:text-white transition-colors rounded-sm border-l-2 border-transparent"
                    >
                      <span className="flex items-center gap-2">
                        {group.icon && <group.icon className="h-3 w-3" />}
                        {group.label}
                      </span>
                      <ChevronDown
                        className={[
                          "h-3 w-3 transition-transform",
                          isOpen ? "rotate-180" : "",
                        ].filter(Boolean).join(" ")}
                      />
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {group.icon && <group.icon className="h-3 w-3" />}
                      <span>{group.label}</span>
                    </div>
                  ))}

                {isOpen && (
                  <ul className="space-y-0.5 px-0">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const badgeValue = item.showBadge ? pendingCount : 0;
                      if (item.disabled) {
                        return (
                          <li key={item.to}>
                            <div
                              className={`group flex items-center gap-2.5 py-1.5 text-sm rounded-sm border-l-2 border-transparent text-slate-500 cursor-not-allowed ${hasLabel ? "pl-8 pr-2.5" : "px-2.5"}`}
                              title="Coming soon"
                            >
                              <Icon className="h-4 w-4 shrink-0" />
                              <span className="flex-1 truncate">{item.label}</span>
                              <span className="ml-auto text-[9px] uppercase tracking-wider text-slate-500">Soon</span>
                            </div>
                          </li>
                        );
                      }
                      return (
                        <li key={item.to}>
                          <NavLink
                            to={item.to}
                            end={item.end}
                            className={({ isActive }) =>
                              [
                                "group flex items-center gap-2.5 py-1.5 text-sm rounded-sm border-l-2 transition-colors",
                                hasLabel ? "pl-8 pr-2.5" : "px-2.5",
                                isActive
                                  ? "border-amber-500 bg-slate-800 text-white"
                                  : "border-transparent text-slate-300 hover:bg-slate-800/60 hover:text-white",
                              ].join(" ")
                            }
                          >
                            <Icon className="h-4 w-4 shrink-0" />
                            <span className="flex-1 truncate">{item.label}</span>
                            {item.showBadge && badgeValue > 0 && (
                              <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500 text-slate-950">
                                {badgeValue}
                              </span>
                            )}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-3 py-3 border-t border-slate-800 space-y-2">
          <div className="text-xs text-slate-400 truncate" title={email}>
            {email}
          </div>
          <Link
            to="/profile"
            className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-sm hover:bg-slate-800 text-slate-300 hover:text-white transition-colors"
          >
            <User className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{displayName || email}</span>
          </Link>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-xs rounded-sm bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="pl-60 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-10 h-14 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-6">
          <h1 className="text-base font-semibold text-white">{resolvedTitle}</h1>
          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-slate-700 text-slate-300 border border-slate-600">
            Superuser
          </span>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">{children ?? <Outlet />}</main>
      </div>
    </div>
  );
}
